import React, { useState, useEffect, useRef, useCallback } from 'react';
import axiosInstance from './services/axiosInstance';
import { useAuth } from './AuthContext';

/* Selettore azienda per la creazione/modifica evento.
 *
 * Due comportamenti distinti:
 *  - ADMIN: non possiede aziende, quindi le sceglie tutte tramite
 *    autocompletamento su /azienda/elenco (endpoint @isAdmin).
 *  - AZIENDA: la relazione e' 1:1, l'unica azienda viene selezionata da sola
 *    e mostrata in sola lettura. Nessun campo da compilare.
 *
 * Nota: la tipologia dell'azienda NON limita le tipologie di evento creabili.
 * Viene riportata solo a scopo informativo.
 */

const codiceTipologia = (azienda) => {
  // AziendaDTO.tipologie e' un OGGETTO singolo, non un array:
  // chiamarci .map() sopra lanciava un TypeError silenzioso.
  if (!azienda || !azienda.tipologie) return '';
  return azienda.tipologie.code || '';
};

/* idAziendaFissa: quando il contesto conosce gia' l'azienda — per esempio la
 * scheda "Crea offerta" dentro la pagina di un utente — non ha senso farla
 * cercare. Viene caricata e mostrata in sola lettura, anche per un admin. */
const SelectAziende = ({ onSelectedData, idAziendaFissa }) => {
  const { userRoles } = useAuth();
  const isAdminLibero =
    Array.isArray(userRoles) && userRoles.includes('ROLE_ADMIN') && !idAziendaFissa;
  const isAdmin = isAdminLibero;
  const [aziendaScelta, setAziendaScelta] = useState(null);
  const [ricerca, setRicerca] = useState('');
  const [risultati, setRisultati] = useState([]);
  const [apertoElenco, setApertoElenco] = useState(false);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState('');

  const contenitoreRef = useRef(null);

  const selezionaAzienda = useCallback((azienda) => {
    setAziendaScelta(azienda);
    setApertoElenco(false);
    setRicerca(azienda.nomeAzienda || '');
    onSelectedData({
      id: azienda.idAzienda,
      tipologia: codiceTipologia(azienda)
    });
  }, [onSelectedData]);

  // Azienda gia' nota dal contesto, oppure unica azienda dell'utente (1:1).
  useEffect(() => {
    let annullato = false;

    if (isAdmin) {
      setCaricamento(false);
      return;
    }

    const richiesta = idAziendaFissa
      ? axiosInstance.get(`/azienda/${idAziendaFissa}`).then(({ data }) => data)
      : axiosInstance.get('/v1/user/aziende').then(({ data }) =>
          Array.isArray(data) && data.length > 0 ? data[0] : null);

    richiesta
      .then((azienda) => {
        if (annullato) return;
        if (azienda) {
          selezionaAzienda(azienda);
        } else {
          setErrore('Nessuna azienda collegata a questo utente.');
        }
      })
      .catch(() => {
        if (!annullato) setErrore("Impossibile recuperare i dati dell'azienda.");
      })
      .finally(() => {
        if (!annullato) setCaricamento(false);
      });

    return () => { annullato = true; };
  }, [selezionaAzienda, isAdmin, idAziendaFissa]);

  // Autocompletamento admin, con attesa per non interrogare a ogni tasto.
  useEffect(() => {
    if (!isAdmin || !apertoElenco) return;

    const attesa = setTimeout(() => {
      axiosInstance.get('/azienda/elenco', { params: { ricerca } })
        .then(({ data }) => setRisultati(Array.isArray(data) ? data : []))
        .catch(() => setErrore('Ricerca aziende non riuscita.'));
    }, 250);

    return () => clearTimeout(attesa);
  }, [ricerca, isAdmin, apertoElenco]);

  // Chiude l'elenco cliccando fuori.
  useEffect(() => {
    const fuori = (e) => {
      if (contenitoreRef.current && !contenitoreRef.current.contains(e.target)) {
        setApertoElenco(false);
      }
    };
    document.addEventListener('mousedown', fuori);
    return () => document.removeEventListener('mousedown', fuori);
  }, []);

  if (caricamento) {
    return <p className='selettore-azienda__stato'>Caricamento azienda…</p>;
  }

  // --- Utente azienda: relazione 1:1, sola lettura ---
  if (!isAdmin) {
    if (errore) {
      return <p className='selettore-azienda__stato selettore-azienda__stato--errore'>{errore}</p>;
    }
    return (
      <div className='selettore-azienda__fissa'>
        <span className='selettore-azienda__etichetta'>Azienda</span>
        <span className='selettore-azienda__nome'>
          {aziendaScelta ? aziendaScelta.nomeAzienda : '—'}
        </span>
      </div>
    );
  }

  // --- Admin: autocompletamento ---
  return (
    <div className='selettore-azienda' ref={contenitoreRef}>
      <input
        type='text'
        className='selettore-azienda__input'
        placeholder='Cerca azienda per nome…'
        value={ricerca}
        onChange={(e) => {
          setRicerca(e.target.value);
          setApertoElenco(true);
          setAziendaScelta(null);
        }}
        onFocus={() => setApertoElenco(true)}
        autoComplete='off'
        aria-label='Cerca azienda'
      />

      {apertoElenco && (
        <ul className='selettore-azienda__elenco'>
          {risultati.length === 0 ? (
            <li className='selettore-azienda__vuoto'>Nessuna azienda trovata</li>
          ) : (
            risultati.map((a) => (
              <li key={a.idAzienda}>
                <button
                  type='button'
                  className='selettore-azienda__voce'
                  onClick={() => selezionaAzienda(a)}
                >
                  <span className='selettore-azienda__voce-nome'>{a.nomeAzienda}</span>
                  {a.comune && (
                    <span className='selettore-azienda__voce-comune'>{a.comune}</span>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      )}

      {aziendaScelta && (
        <p className='selettore-azienda__conferma'>
          Selezionata: <strong>{aziendaScelta.nomeAzienda}</strong>
        </p>
      )}

      {errore && (
        <p className='selettore-azienda__stato selettore-azienda__stato--errore'>{errore}</p>
      )}
    </div>
  );
};

export default SelectAziende;
